// Singleton webSocketManager for maintaining a single WebSocket connection for a project.
// This is useful for multiple components that want to receive or publish updates to the
// websocket server (e.g editor chat, inline editor, funnels manager) with a single connection.

import type {
  ClientToServerWebsocketCallbackData,
  ServerToClientWebsocketBroadcastData,
} from "schemas/generated/websocket";

import { WEBSOCKET_BASE_URL } from "@v2/utils/env";
import {
  clientToServerWebsocketCallbackDataSchema,
  serverToClientWebsocketBroadcastDataSchema,
} from "schemas/websocket";

interface Subscriber {
  id: string;
  onUpdate: (data: ServerToClientWebsocketBroadcastData) => void;
}

interface Connection {
  socket: WebSocket;
  subscribers: Map<string, Subscriber>;
  pendingMessages: Set<ClientToServerWebsocketCallbackData>;
}

// Map of projectId to Connection for subscribing to multiple projects
const connections = new Map<string, Connection>();

export const WebSocketManager = Object.freeze({
  /**
   * Subscribes a component to WebSocket updates for a given projectId.
   * Creates a new WebSocket connection if one doesn't exist.
   * Returns a subscription ID that should be used to unsubscribe later.
   */
  subscribe: (
    projectId: string,
    jwt: string,
    onUpdateCallback: (data: ServerToClientWebsocketBroadcastData) => void,
  ): string => {
    if (!projectId || !jwt) {
      console.warn(
        "[WSManager] Attempted to subscribe to WebSocket with no projectId, userId, or JWT.",
      );
      return "";
    }

    let connection = connections.get(projectId);

    if (!connection) {
      const wsUrl = `${WEBSOCKET_BASE_URL}?projectId=${projectId}&userToken=${jwt}`;
      const socket = new WebSocket(wsUrl);

      connection = {
        socket,
        subscribers: new Map(),
        pendingMessages: new Set(),
      };

      connections.set(projectId, connection);

      // Send any pending messages when the socket opens
      socket.addEventListener("open", () => {
        if (connection) {
          for (const msg of connection.pendingMessages) {
            connection.socket.send(JSON.stringify(msg));
          }
          connection.pendingMessages.clear();
        }
      });

      socket.addEventListener("error", (error) => {
        console.error("[WSManager] WebSocket error for:", projectId, error);
      });

      socket.addEventListener("close", () => {
        connections.delete(projectId);
      });

      socket.addEventListener("message", (event) => {
        const data = JSON.parse(event.data as string);
        const validatedData =
          serverToClientWebsocketBroadcastDataSchema.parse(data);

        const currentConnection = connections.get(projectId);
        if (currentConnection) {
          currentConnection.subscribers.forEach((subscriber) => {
            try {
              subscriber.onUpdate(validatedData);
            } catch (error) {
              console.error(
                "[WSManager] Error in onUpdateCallback for subscriber:",
                error,
              );
            }
          });
        }
      });
    }

    const subscriptionId = `${projectId}-${Date.now()}-${Math.random().toString()}`;
    connection.subscribers.set(subscriptionId, {
      id: subscriptionId,
      onUpdate: onUpdateCallback,
    });

    return subscriptionId;
  },

  /**
   * Unsubscribes a component using the subscriptionId and closes the
   * Websocket connection if no subscribers remain.
   */
  unsubscribe: (projectId: string, subscriptionId: string) => {
    if (!projectId || !subscriptionId) {
      console.warn(
        "[WSManager] Attempted to unsubscribe with invalid parameters.",
      );
      return;
    }

    const connection = connections.get(projectId);
    if (!connection) {
      console.warn(
        "[WSManager] No connection found to unsubscribe for:",
        projectId,
      );
      return;
    }

    connection.subscribers.delete(subscriptionId);

    if (connection.subscribers.size <= 0) {
      connection.socket.close();
      connections.delete(projectId);
    }
  },

  getConnection: (projectId: string): Connection | undefined => {
    return connections.get(projectId);
  },

  publishUpdate: (
    projectId: string,
    data: ClientToServerWebsocketCallbackData,
  ) => {
    try {
      // Validate the data before sending
      const validatedData =
        clientToServerWebsocketCallbackDataSchema.parse(data);

      const connection = connections.get(projectId);
      if (connection) {
        if (connection.socket.readyState === WebSocket.OPEN) {
          connection.socket.send(JSON.stringify(validatedData));
        } else {
          connection.pendingMessages.add(validatedData);
        }
      }
    } catch (error) {
      console.error("[WSManager] Invalid data for publishing:", error);
    }
  },
});
